import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import {
  ENTERPRISE_DEPARTMENTS,
  ENTERPRISE_PLANTS,
  ROLE_METADATA,
} from '@/config/user-management.config';
import { Role, User, UserUpdateInput } from '@/types/domain/user';
import { mockUserManagementService } from '@/services/mock/MockUserManagementService';
import { formatInr } from '@/lib/utils';
import {
  X,
  Edit2,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { showToast } from '@/context/NotificationContext';

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onUserUpdated: (user: User) => void;
  existingUsers: User[];
  currentAdmin?: User | null;
}

export const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdated,
  existingUsers,
  currentAdmin,
}) => {
  if (!isOpen || !user) return null;

  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone || '');
  const [departmentCode, setDepartmentCode] = useState(user.departmentCode);
  const [plantCode, setPlantCode] = useState(user.plantCode);
  const [managerId, setManagerId] = useState(user.managerId || 'usr-2');
  const [role, setRole] = useState<Role>(user.role);
  const [approvalAuthorityLimit, setApprovalAuthorityLimit] = useState<number>(user.approvalAuthorityLimit);

  const [confirmedRoleChange, setConfirmedRoleChange] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const availableRoles = (Object.keys(ROLE_METADATA) as Role[]).filter(
    (r) => !ROLE_METADATA[r].isSystemControlled
  );

  const potentialManagers = existingUsers.filter(
    (u) =>
      u.id !== user.id &&
      (u.role === 'MANAGER' || u.role === 'PROCUREMENT_MANAGER' || u.role === 'MANAGEMENT' || u.role === 'ADMIN')
  );

  const isRoleChanged = role !== user.role;

  const handleRoleChange = (newRole: Role) => {
    setRole(newRole);
    setApprovalAuthorityLimit(ROLE_METADATA[newRole].defaultLimit);
    setConfirmedRoleChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!name.trim()) {
      setFormError('Full name is required.');
      return;
    }

    if (isRoleChanged && !confirmedRoleChange) {
      setFormError('You must explicitly confirm the role change before saving.');
      return;
    }

    try {
      setIsSubmitting(true);
      const dept = ENTERPRISE_DEPARTMENTS.find((d) => d.code === departmentCode);
      const input: UserUpdateInput = {
        name: name.trim(),
        phone: phone.trim() || user.phone,
        designation: ROLE_METADATA[role]?.title || user.designation,
        departmentCode,
        plantCode,
        companyCode: user.companyCode || '1000',
        costCenterCode: dept?.costCenter || user.costCenterCode || 'CC-1010',
        managerId,
        role,
        approvalAuthorityLimit,
        dataScopeOwnership: role === 'MANAGEMENT' || role === 'ADMIN' ? 'ENTERPRISE' : 'DEPARTMENT',
        scopedDepartments: [departmentCode],
        scopedPlants: [plantCode],
      };

      const updated = await mockUserManagementService.updateUser(
        user.id,
        input,
        currentAdmin ? { id: currentAdmin.id, email: currentAdmin.email, role: currentAdmin.role } : undefined
      );

      onUserUpdated(updated);
      showToast(
        'User Profile Updated',
        `Changes saved for ${updated.name} (${updated.employeeId}).`,
        'success'
      );
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update user.';
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 font-poppins">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-card border border-border w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden z-10 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/70 bg-muted/20">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Edit2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-foreground tracking-tight">
                Edit User Identity & Authority
              </h2>
              <p className="text-[11px] text-foreground-secondary">
                <span className="font-mono font-bold text-foreground">{user.employeeId}</span> • {user.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-foreground-muted hover:text-foreground transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Compact Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-3.5 text-xs">
          {formError && (
            <div className="p-2.5 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive flex items-center gap-2 text-xs font-semibold">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* Full Name */}
            <div className="space-y-1">
              <label className="font-bold text-foreground text-[11px]">Full Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              />
            </div>

            {/* Direct Phone */}
            <div className="space-y-1">
              <label className="font-bold text-foreground text-[11px]">Direct Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              />
            </div>

            {/* Assigned Role */}
            <div className="space-y-1">
              <label className="font-bold text-foreground text-[11px]">Assigned Role</label>
              <select
                value={role}
                onChange={(e) => handleRoleChange(e.target.value as Role)}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs font-semibold focus:ring-1 focus:ring-primary focus:outline-none"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_METADATA[r].title}
                  </option>
                ))}
              </select>
            </div>

            {/* DOA Approval Limit */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="font-bold text-foreground text-[11px]">Approval Limit (₹)</label>
                <span className="text-[10px] text-emerald-600 font-bold font-mono">
                  {formatInr(approvalAuthorityLimit)}
                </span>
              </div>
              <input
                type="number"
                min="0"
                step="5000"
                value={approvalAuthorityLimit}
                onChange={(e) => setApprovalAuthorityLimit(Number(e.target.value))}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background font-mono text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              />
            </div>

            {/* Department */}
            <div className="space-y-1">
              <label className="font-bold text-foreground text-[11px]">Department</label>
              <select
                value={departmentCode}
                onChange={(e) => setDepartmentCode(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              >
                {ENTERPRISE_DEPARTMENTS.map((dept) => (
                  <option key={dept.code} value={dept.code}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Plant Facility */}
            <div className="space-y-1">
              <label className="font-bold text-foreground text-[11px]">Operating Plant</label>
              <select
                value={plantCode}
                onChange={(e) => setPlantCode(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              >
                {ENTERPRISE_PLANTS.map((plant) => (
                  <option key={plant.code} value={plant.code}>
                    {plant.name.split('—')[0]} ({plant.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Reporting Manager */}
            <div className="sm:col-span-2 space-y-1">
              <label className="font-bold text-foreground text-[11px]">Direct Reporting Manager</label>
              <select
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
              >
                {potentialManagers.map((mgr) => (
                  <option key={mgr.id} value={mgr.id}>
                    {mgr.name} ({mgr.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* SENSITIVE ROLE CHANGE CONFIRMATION BANNER */}
          {isRoleChanged && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-300 space-y-2 animate-in fade-in duration-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-[11px] leading-relaxed">
                  <strong>Role Transition Detected:</strong> Changing from <span className="underline font-bold">{user.role}</span> to{' '}
                  <span className="underline font-bold">{role}</span> will reconfigure transactional permissions and DOA thresholds.
                </div>
              </div>

              <label className="flex items-center gap-2 font-semibold text-[11px] text-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmedRoleChange}
                  onChange={(e) => setConfirmedRoleChange(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-amber-500 text-primary focus:ring-primary"
                />
                <span>I confirm this role transition and DOA authorization.</span>
              </label>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/60">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl font-bold text-xs h-8 cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={isSubmitting}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-8 cursor-pointer shadow-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
